import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { type SchmancyTone, toneColors } from '@schmancy/theme/tone'
import { css, html } from 'lit'
import { property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'

/**
 * How many marks the band ever renders — as many neighbours as still read as a
 * neighbourhood rather than as a texture. A legibility bound, not a fit to any
 * one consumer's column: seven marks span 70px, which clears the narrowest
 * column a consumer has (328px on a phone, less its fraction label) several
 * times over.
 */
const WINDOW = 7

/**
 * Which of N you are on, as a row of marks — one bounded window at every N.
 *
 * A mark per item is honest up to a dozen and unusable past it: 400 tickets
 * draw a 4000px band nothing can hold, and the obvious repair — a
 * proportional track whose thumb spans `1/N` — moves 0.3px per step at that
 * N, which is a control that reports nothing. This renders `min(max, 7)`
 * marks instead, so the band is bounded but never padded: two items get
 * exactly two marks, four hundred get seven.
 *
 * The property that makes it work is that per-step motion is one full mark
 * pitch at every `max`: the active mark walks the band, and the band turns a
 * page when the mark runs out of room. A window that re-centres on the mark
 * every step instead was tried and rejected on the evidence — at max=400 it
 * paints a byte-identical band before and after a step, since every mark is
 * interchangeable with its neighbour, which is the same defect as the 0.3px
 * proportional thumb with the motion rounded all the way down to nothing.
 *
 * Exact global position is the caller's fraction label, slotted beside the
 * marks and announced.
 *
 * Output only: the marks carry no hit target — a 6px dot cannot hold one —
 * so they are `aria-hidden` decoration beside a live region that already
 * says the position in words.
 */
@schmancyElement('schmancy-slide-indicator')
export class SchmancySlideIndicator extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-flex;
				align-items: center;
				flex-shrink: 0;
			}
		`,
	]

	/**
	 * Which item is current, 1-based — the origin `<schmancy-steps>` uses and
	 * the one a caller's own "3 of 25" label is already counting in. Outside
	 * `1..max` it clamps.
	 */
	@property({ type: Number }) value = 0

	/** How many items there are. `1` or fewer renders no marks at all. */
	@property({ type: Number }) max = 0

	/**
	 * Which tone the marks carry. `neutral` by default: position is not a
	 * status, so the row takes the page's own ink rather than announcing
	 * itself in the brand colour.
	 */
	@property({ type: String, reflect: true }) color: SchmancyTone = 'neutral'

	/**
	 * Whether the slotted label renders before the marks rather than after.
	 *
	 * The band's width is `min(max, 7)` pitches, so it is the one part of this
	 * component that changes size when `max` does. A consumer whose `max` is
	 * fixed leaves this alone. A consumer whose `max` changes at runtime — a
	 * rail whose scope switch rescopes the set under the reader's own finger —
	 * sets it, so the band's change falls in the empty space past the label
	 * instead of pushing the label sideways.
	 */
	@property({ type: Boolean, attribute: 'label-first' }) labelFirst = false

	/** Whether the slot holds anything — an absent label is given no room. */
	@state() private labelled = false

	render() {
		const max = Math.max(0, Math.trunc(this.max))
		const span = max > 1 ? Math.min(max, WINDOW) : 0
		const value = Math.min(Math.max(Math.round(this.value), 1), Math.max(max, 1))
		// Pages overlap by one mark, so the item a page turn lands on was already
		// on screen as the far edge of the page before it, and the last page pins
		// to the end of the set rather than running off it.
		const start = Math.min(Math.floor((value - 1) / (WINDOW - 1)) * (WINDOW - 1) + 1, Math.max(max - span + 1, 1))
		// One colour decision for the whole row: the resting marks are this same
		// ink at a third of its weight, so the band never disagrees with itself
		// and reads on whatever ground the host painted.
		const ink = toneColors(this.color, 'solid').backgroundColor

		const marks = when(
			span > 0,
			() => html`
				<div part="marks" aria-hidden="true" class="flex items-center">
					${repeat(
						Array.from({ length: span }, (_, offset) => start + offset),
						index => index,
						index => {
							// An outermost mark shrinks only on a side that is actually
							// clipping — a shrunken end where nothing follows would promise
							// items that do not exist. The active mark is never the one
							// shrunk: a page turn can land it on a clipping edge, and the
							// mark the whole row is about has to stay the legible one.
							const clipped =
								index !== value && ((index === start && index > 1) || (index === start + span - 1 && index < max))
							return html`
								<span class="flex h-2.5 w-2.5 shrink-0 items-center justify-center">
									<span
										class="${this.classMap({
											'rounded-full transition-colors duration-200': true,
											'h-1.5 w-1.5': !clipped,
											'h-1 w-1': clipped,
											'opacity-100': index === value,
											'opacity-30': index !== value,
										})}"
										style="${this.styleMap({ backgroundColor: ink })}"
									></span>
								</span>
							`
						},
					)}
				</div>
			`,
		)

		// The live region is the label's own box, so it survives the reorder and no
		// consumer putting its numeral first has to hand-roll one.
		const label = html`
			<div
				part="label"
				role="status"
				aria-live="polite"
				class="${this.classMap({
					'ms-2': span > 0 && this.labelled && !this.labelFirst,
					'me-2': span > 0 && this.labelled && this.labelFirst,
				})}"
			>
				<slot
					@slotchange=${(e: Event) => {
						this.labelled = (e.target as HTMLSlotElement)
							.assignedNodes({ flatten: true })
							.some(node => node.nodeType === Node.ELEMENT_NODE || (node.textContent ?? '').trim() !== '')
					}}
				></slot>
			</div>
		`

		return when(
			this.labelFirst,
			() => html`${label}${marks}`,
			() => html`${marks}${label}`,
		)
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-slide-indicator': SchmancySlideIndicator
	}
}
